import React, { useEffect, useState } from 'react';
import { Search, ExternalLink, CheckCircle2, AlertCircle, Circle } from 'lucide-react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Per-business SEO progress rollup. Pulls each business's
// os_seo_checklist, os_seo_citations, os_seo_review_snapshots, and
// os_seo_settings/reviews directly from Firestore. Read-only — drives
// you into each business's OS for actual edits.
//
// Expected URL convention: businesses can declare `internalOsUrl` on
// their document to deep-link into the OS. Falls back to
// https://khaonoigym-internal-ostesting.netlify.app for KNG.

const KNG_BUSINESS_ID = 'RXpfgi8K2TT3rD5AmtuN';
const KNG_OS_URL = 'https://khaonoigym-internal-ostesting.netlify.app';

function deriveOsUrl(biz) {
  if (biz.internalOsUrl) return biz.internalOsUrl;
  if (biz.id === KNG_BUSINESS_ID) return KNG_OS_URL;
  return null;
}

function statusIcon(pct) {
  if (pct >= 80) return <CheckCircle2 size={14} className="seo-rollup-icon-good" />;
  if (pct >= 40) return <AlertCircle size={14} className="seo-rollup-icon-warn" />;
  return <Circle size={14} className="seo-rollup-icon-todo" />;
}

function statusLabel(pct) {
  if (pct >= 80) return 'On track';
  if (pct >= 40) return 'In progress';
  if (pct > 0) return 'Behind';
  return 'Not started';
}

function fmtPct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

export default function SeoRollupTab({ businesses = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const out = [];
      for (const biz of businesses) {
        try {
          const [checklistSnap, citationsSnap, reviewsSnap, settingsSnap] = await Promise.all([
            getDocs(collection(db, 'businesses', biz.id, 'os_seo_checklist')),
            getDocs(collection(db, 'businesses', biz.id, 'os_seo_citations')),
            getDocs(collection(db, 'businesses', biz.id, 'os_seo_review_snapshots')),
            getDoc(doc(db, 'businesses', biz.id, 'os_seo_settings', 'reviews')),
          ]);

          const checklistTotal = checklistSnap.size;
          let checklistDone = 0;
          checklistSnap.forEach(d => { if (d.data().status === 'done') checklistDone++; });

          const citationsTotal = citationsSnap.size;
          let citationsLive = 0;
          let citationsPending = 0;
          citationsSnap.forEach(d => {
            const s = d.data().status;
            if (s === 'live') citationsLive++;
            else if (s === 'pending') citationsPending++;
          });

          let latestReviewCount = 0;
          let latestReviewDate = null;
          let latestRating = null;
          reviewsSnap.forEach(d => {
            const r = d.data();
            if (!latestReviewDate || (r.date || '') > latestReviewDate) {
              latestReviewDate = r.date;
              latestReviewCount = r.count || 0;
              latestRating = typeof r.rating === 'number' ? r.rating : null;
            }
          });

          const target = settingsSnap.exists() ? (settingsSnap.data().reviewTarget || 50) : 50;

          // Aggregate score: weighted avg of checklist% (40%), citations% (30%), reviews% (30%)
          const checklistPct = fmtPct(checklistDone, checklistTotal || 16);
          const citationsPct = fmtPct(citationsLive, citationsTotal || 12);
          const reviewsPct = fmtPct(latestReviewCount, target);
          const overallPct = Math.round(checklistPct * 0.4 + citationsPct * 0.3 + reviewsPct * 0.3);
          const hasAnyData = checklistTotal > 0 || citationsTotal > 0 || reviewsSnap.size > 0;

          out.push({
            id: biz.id,
            name: biz.name || '(Unnamed)',
            type: biz.type || '',
            osUrl: deriveOsUrl(biz),
            checklistDone,
            checklistTotal,
            checklistPct,
            citationsLive,
            citationsPending,
            citationsTotal,
            citationsPct,
            reviewsCount: latestReviewCount,
            reviewsTarget: target,
            reviewsPct,
            latestRating,
            latestReviewDate,
            overallPct,
            hasAnyData,
          });
        } catch (e) {
          console.warn('SeoRollupTab: failed for biz', biz.id, e);
          out.push({
            id: biz.id,
            name: biz.name || '(Unnamed)',
            type: biz.type || '',
            osUrl: deriveOsUrl(biz),
            error: true,
            hasAnyData: false,
          });
        }
        if (cancelled) return;
      }
      out.sort((a, b) => (b.overallPct || 0) - (a.overallPct || 0));
      if (!cancelled) {
        setRows(out);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businesses]);

  const totals = rows.reduce(
    (acc, r) => {
      if (!r.hasAnyData) return acc;
      acc.businesses++;
      acc.checklistDone += r.checklistDone || 0;
      acc.checklistTotal += r.checklistTotal || 0;
      acc.citationsLive += r.citationsLive || 0;
      acc.citationsTotal += r.citationsTotal || 0;
      acc.reviewsCount += r.reviewsCount || 0;
      acc.reviewsTarget += r.reviewsTarget || 0;
      return acc;
    },
    { businesses: 0, checklistDone: 0, checklistTotal: 0, citationsLive: 0, citationsTotal: 0, reviewsCount: 0, reviewsTarget: 0 }
  );

  return (
    <div className="seo-rollup">
      <style>{`
        .seo-rollup { padding: 24px 0; }
        .seo-rollup-hd { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:18px; flex-wrap:wrap; }
        .seo-rollup-hd-title { display:flex; align-items:center; gap:10px; }
        .seo-rollup-hd-title h2 { margin:0; font-size:22px; font-weight:700; }
        .seo-rollup-hd-sub { color: var(--text-muted, #888); font-size: 13px; margin-top:2px; }
        .seo-rollup-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap:12px; margin-bottom:20px; }
        .seo-rollup-stat { background: var(--surface, #fff); border:1px solid var(--border, #e5e7eb); border-radius:10px; padding:14px 16px; }
        .seo-rollup-stat-lbl { font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color: var(--text-muted, #888); margin-bottom:4px; }
        .seo-rollup-stat-val { font-size:22px; font-weight:800; color: var(--text, #111); line-height:1.1; }
        .seo-rollup-stat-sub { font-size:11px; color: var(--text-muted-light, #aaa); margin-top:4px; }
        .seo-rollup-table { width:100%; background: var(--surface, #fff); border:1px solid var(--border, #e5e7eb); border-radius:10px; border-collapse: separate; border-spacing:0; overflow:hidden; }
        .seo-rollup-table th { text-align:left; padding:10px 14px; font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color: var(--text-muted, #888); background:#fafafa; border-bottom:1px solid var(--border, #e5e7eb); }
        .seo-rollup-table td { padding:14px; border-top:1px solid var(--border, #e5e7eb); font-size:13px; vertical-align: middle; }
        .seo-rollup-table tr:first-child td { border-top:none; }
        .seo-rollup-name { font-weight:700; color: var(--text, #111); }
        .seo-rollup-type { font-size:11px; color: var(--text-muted, #888); margin-top:2px; }
        .seo-rollup-bar { width:100%; height:6px; background:#f3f4f6; border-radius:3px; overflow:hidden; margin-top:4px; }
        .seo-rollup-bar-fill { height:100%; background:#10b981; transition: width .25s; }
        .seo-rollup-status { display:inline-flex; align-items:center; gap:6px; font-weight:600; }
        .seo-rollup-icon-good { color:#10b981; }
        .seo-rollup-icon-warn { color:#f59e0b; }
        .seo-rollup-icon-todo { color:#9ca3af; }
        .seo-rollup-link { color:#1d4ed8; text-decoration:none; font-weight:600; display:inline-flex; align-items:center; gap:4px; }
        .seo-rollup-link:hover { text-decoration: underline; }
        .seo-rollup-empty { padding:48px 24px; text-align:center; color:var(--text-muted, #888); }
        .seo-rollup-empty p { margin:6px 0; }
      `}</style>

      <div className="seo-rollup-hd">
        <div className="seo-rollup-hd-title">
          <Search size={22} />
          <div>
            <h2>SEO Roll-up</h2>
            <div className="seo-rollup-hd-sub">Portfolio-level view across every business with the SEO module installed</div>
          </div>
        </div>
      </div>

      <div className="seo-rollup-stats">
        <div className="seo-rollup-stat">
          <div className="seo-rollup-stat-lbl">Active</div>
          <div className="seo-rollup-stat-val">{totals.businesses}</div>
          <div className="seo-rollup-stat-sub">businesses with SEO data</div>
        </div>
        <div className="seo-rollup-stat">
          <div className="seo-rollup-stat-lbl">Checklist</div>
          <div className="seo-rollup-stat-val">{totals.checklistTotal ? Math.round((totals.checklistDone / totals.checklistTotal) * 100) : 0}%</div>
          <div className="seo-rollup-stat-sub">{totals.checklistDone} of {totals.checklistTotal} tasks done</div>
        </div>
        <div className="seo-rollup-stat">
          <div className="seo-rollup-stat-lbl">Citations</div>
          <div className="seo-rollup-stat-val">{totals.citationsLive}</div>
          <div className="seo-rollup-stat-sub">live across {totals.citationsTotal} directories</div>
        </div>
        <div className="seo-rollup-stat">
          <div className="seo-rollup-stat-lbl">Reviews</div>
          <div className="seo-rollup-stat-val">{totals.reviewsCount}</div>
          <div className="seo-rollup-stat-sub">across all businesses (target {totals.reviewsTarget})</div>
        </div>
      </div>

      {loading && (
        <div className="seo-rollup-empty">
          <p>Loading SEO progress across your portfolio…</p>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="seo-rollup-empty">
          <p>No businesses to display.</p>
          <p>Add a business to your portfolio to start tracking SEO progress.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <table className="seo-rollup-table">
          <thead>
            <tr>
              <th>Business</th>
              <th>Status</th>
              <th>Checklist</th>
              <th>Citations</th>
              <th>Reviews</th>
              <th>Overall</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>
                  <div className="seo-rollup-name">{r.name}</div>
                  {r.type && <div className="seo-rollup-type">{r.type}</div>}
                </td>
                <td>
                  {r.error ? (
                    <span className="seo-rollup-status" style={{ color: '#ef4444' }}>
                      <AlertCircle size={14} /> Read failed
                    </span>
                  ) : !r.hasAnyData ? (
                    <span className="seo-rollup-status" style={{ color: '#9ca3af' }}>
                      <Circle size={14} /> Not started
                    </span>
                  ) : (
                    <span className="seo-rollup-status">
                      {statusIcon(r.overallPct)} {statusLabel(r.overallPct)}
                    </span>
                  )}
                </td>
                <td>
                  {r.hasAnyData ? (
                    <>
                      <div>{r.checklistDone} / {r.checklistTotal}</div>
                      <div className="seo-rollup-bar"><div className="seo-rollup-bar-fill" style={{ width: r.checklistPct + '%' }} /></div>
                    </>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td>
                  {r.hasAnyData ? (
                    <>
                      <div>{r.citationsLive} live{r.citationsPending ? ` · ${r.citationsPending} pending` : ''}</div>
                      <div className="seo-rollup-bar"><div className="seo-rollup-bar-fill" style={{ width: r.citationsPct + '%' }} /></div>
                    </>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td>
                  {r.hasAnyData && r.latestReviewDate ? (
                    <>
                      <div>{r.reviewsCount} / {r.reviewsTarget} {r.latestRating != null ? `· ${r.latestRating.toFixed(1)}★` : ''}</div>
                      <div className="seo-rollup-bar"><div className="seo-rollup-bar-fill" style={{ width: Math.min(100, r.reviewsPct) + '%' }} /></div>
                    </>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td>
                  {r.hasAnyData ? (
                    <strong>{r.overallPct}%</strong>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {r.osUrl ? (
                    <a href={r.osUrl} target="_blank" rel="noopener noreferrer" className="seo-rollup-link">
                      Open OS <ExternalLink size={12} />
                    </a>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>No OS URL</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-muted, #888)', textAlign: 'center' }}>
        Read-only view. Edit checklist / citations / review snapshots inside each business's OS.
      </div>
    </div>
  );
}
